import { Component, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../auth.service';
import { environment } from 'environments/environment';
import { AuthActivateQuestionsService } from '../auth-activate-questions.service';
import { DynamicFormComponent } from 'app/core';
import { SERVICES } from '@core/assets/constants';
import { ThemeService } from '@core/services';

const { contentful } = environment;
/**
 * Account activation or password reset form, depending on the operation
 * indicated in the route (`activation` | `reset`). Validates the token received
 * by email and, once the operation is complete, automatically logs into the account.
 */
@Component({
  selector: 'app-account-password',
  templateUrl: './account-password.component.html',
  styleUrls: ['./account-password.component.scss']
})
export class AccountPasswordComponent implements OnInit {
  @ViewChild(DynamicFormComponent, { static: false }) public dynamicForm: DynamicFormComponent;
  serviceName: string;
  credentials = {
    username: '',
    password: '',
    sid: '',
    captchaValue: '',
    token: '',
    clientId: ''
  };
  public questions: any[] = [];
  public isContentfulActive = contentful.active;
  public data = {
    sid: '',
    clientId: '',
    token: '',
    userName: '',
    name: '',
    surname: '',
    lang: 'es',
    // pid: null,
    // alias: null,
    password: '',
    passwordConfirm: '',
    operation: '',
    disclaimerPrivacyAndTerms: false,
    disclaimerMarketing: false,
    clientDisclaimerPrivacy: false
  };

  public password: string = '';
  public operation: string;
  public supportUrl: string;
  public error: any;
  public loading = true;
  public done = false;
  private title: string;
  public logo: string;
  public clientTermsUrl: string;
  public clientName: string;

  constructor(
    private authService: AuthService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private authActivateQuestionsService: AuthActivateQuestionsService,
    private titleService: Title,
    private themeService: ThemeService
  ) {}

  /**
   * Resolves the service (sid) from the query params or a credentials token,
   * and validates the activation/reset token received by email.
   */
  public ngOnInit(): void {
    this.route.queryParamMap.subscribe(async (queryParams: ParamMap) => {
      if (queryParams.get('credentials')) {
        this.credentials.token = queryParams.get('credentials');
        const payload = await this.authService.verifyToken(this.credentials.token);
        this.credentials.sid = payload.sid;
        this.credentials.username = payload.email;
        this.credentials.clientId = payload.clientId;
        this.credentials.token = null;
      } else {
        this.credentials.sid = queryParams.get('sid');
        this.credentials.username = queryParams.get('username');
      }

      this.serviceName =
        this.credentials.sid && SERVICES[this.credentials.sid]
          ? SERVICES[this.credentials.sid]
          : 'campus';
      this.themeService.setTheme(this.serviceName);
    });
    this.authService.logout();
    this.title = this.route.snapshot.data.title || 'auth.changePassword';
    this.operation = this.route.snapshot.data.operation;
    if (this.operation === 'reset') {
      this.data.disclaimerPrivacyAndTerms = true;
    }
    this.data.token = this.route.snapshot.queryParamMap.get('token');
    this.data.clientId = this.route.snapshot.queryParamMap.get('clientId');
    this.data.sid = this.route.snapshot.queryParamMap.get('sid');

    this.translate.get(this.title).subscribe((trans) => this.titleService.setTitle(trans));
    this.translate.get('urls.support').subscribe((trans) => (this.supportUrl = trans));

    this.authService.validateAccountPasswordToken(this.data.token, this.operation).subscribe({
      next: (validation) => {
        this.loading = false;
        this.logo = validation.logo;
        this.data.userName = validation.userName;

        if (sessionStorage.getItem('activationData')) {
          const lang = validation?.lang || this.translate.getBrowserLang();
          const { clientTermsUrls, clientName } = JSON.parse(
            sessionStorage.getItem('activationData')
          );
          this.clientTermsUrl = clientTermsUrls[lang]
            ? clientTermsUrls[lang]
            : clientTermsUrls['en'];
          this.clientName = clientName;
        }

        this.questions = this.authActivateQuestionsService.getQuestions(
          { ...this.data, ...validation },
          this.operation,
          this.serviceName
        );
      },
      error: (error) => {
        this.onError(error);
      }
    });
  }

  /**
   * Submits the dynamic form's data to activate or reset the account's password.
   */
  onSubmit(): void {
    const data = this.dynamicForm.form.value;
    this.data = {
      ...this.data,
      ...data,
      complianceDate: Date.now(),
      clientComplianceDate: this.data.clientDisclaimerPrivacy ? Date.now() : undefined,
      marketingComplianceDate: this.data.disclaimerMarketing ? Date.now() : undefined
    };
    this.error = null;
    this.loading = true;

    this.authService.setAccountPassword(this.data, this.operation).subscribe({
      next: (response) => {
        if (response && response.service) {
          // Stop redirecting to /entry in order to show errors in form
          this.entry(response.service);
        } else {
          this.loading = false;
          this.done = true;
        }
      },
      error: (error) => {
        this.onError(error);
      }
    });
  }

  /**
   * Automatically authenticates the user after completing the activation/reset
   * and redirects them to the destination service.
   * @param response Service URL returned by the API, with token and sid as query params
   */
  entry(response: any): void {
    this.authService.logout();
    const queryParams = new URL(response).searchParams;
    const token = queryParams.get('token');
    const sid = queryParams.get('sid');
    this.authService.createServiceLogin({ token, sid }).subscribe({
      next: (response) => {
        window.location.replace(response.service);
      },
      error: (error) => {
        this.onError(error);
      }
    });
  }

  /**
   * Indicates whether the submit button should be disabled: invalid form,
   * operation in progress or already completed, or mandatory disclaimers not accepted.
   */
  get submitDisabled(): any {
    if (this.dynamicForm) {
      return (
        this.dynamicForm.form.invalid ||
        this.loading ||
        this.done ||
        (this.clientTermsUrl && !this.data.clientDisclaimerPrivacy) ||
        !this.data.disclaimerPrivacyAndTerms
      );
    }
    return true;
  }

  /**
   * Applies the received error, regenerates the form's questions and invalidates the token.
   * @param error Error returned by the API
   */
  onError(error: any): void {
    this.questions = this.authActivateQuestionsService.getQuestions(
      this.data,
      this.operation,
      this.serviceName
    );
    this.loading = false;
    this.data.token = null;
    this.error = error
      ? Object.assign({}, error, {
          errorCode: `apiErrors.${error.errorCode || 'SYSTEM_ERROR'}`
        })
      : null;
  }
}
